import { useMemo, useState } from "react"
import { toast } from "react-toastify"
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap"
import PageWrapper from "components/Common/PageWrapper"
import { CanView } from "components/Common/CanView"
import { ACCESS } from "constants/accessPermissions"
import { useAccountDailyReport } from "hooks/useAccountDailyReport"
import { useCloseAccountDay } from "hooks/useAccountDayClose"
import { formatCurrency } from "utils/formatCurrency"
import { formatReadableDate } from "utils/formatDate"
import { downloadAndOpenFile } from "api/pdfApi"

const todayDate = () => new Date().toISOString().slice(0, 10)

const AccountDailyReport = () => {
  document.title = "Account Daily Report"

  const [date, setDate] = useState(todayDate())
  const [isExporting, setIsExporting] = useState(false)
  const [closeModal, setCloseModal] = useState(false)
  const [closeNotes, setCloseNotes] = useState("")
  const [overdueCash, setOverdueCash] = useState(null)
  const [acknowledgeOverdue, setAcknowledgeOverdue] = useState(false)

  const queryParams = useMemo(() => ({ date }), [date])
  const { data: response, isLoading, isFetching, refetch } = useAccountDailyReport(queryParams)
  const report = response?.data

  const { mutateAsync: closeDayMutation, isPending: closingDay } = useCloseAccountDay()

  const handleExportPdf = () =>
    downloadAndOpenFile({
      url: "/account-exports/daily-report",
      params: queryParams,
      openInNewTab: false,
      filename: `daily-report-${date}.pdf`,
      onStart: () => setIsExporting(true),
      onEnd: () => setIsExporting(false),
    })

  const openCloseModal = () => {
    setOverdueCash(null)
    setAcknowledgeOverdue(false)
    setCloseNotes("")
    setCloseModal(true)
  }

  const handleConfirmClose = async () => {
    try {
      const res = await closeDayMutation({
        close_date: date,
        notes: closeNotes?.trim() || null,
        acknowledge_overdue: acknowledgeOverdue,
      })
      toast.success(res?.message ?? "Day closed successfully.")
      setCloseModal(false)
      refetch()
    } catch (error) {
      const overdue = error?.response?.data?.errors?.overdue_cash
      if (error?.response?.status === 422 && overdue) {
        setOverdueCash(overdue)
        return
      }
      const msg = error?.response?.data?.message || "Failed to close the day."
      toast.error(msg)
    }
  }

  return (
    <PageWrapper title="Account Daily Report" breadcrumbItem="Daily Report">
      <Row className="g-2 align-items-end mb-3">
        <Col xs={12} sm={4} md={3}>
          <Label className="mb-1 small fw-semibold">Date</Label>
          <Input type="date" bsSize="sm" value={date} onChange={e => setDate(e.target.value)} />
        </Col>
        <Col xs="auto">
          <Button color="light" size="sm" onClick={() => refetch()}>
            <i className={`mdi mdi-refresh ${isFetching ? "mdi-spin" : ""}`} />
          </Button>
        </Col>
        <Col xs="auto" className="ms-auto d-flex gap-2 align-items-center">
          {report?.is_closed ? (
            <Badge color="dark" className="py-2 px-3">
              <i className="mdi mdi-lock-outline me-1" />
              Day Closed
            </Badge>
          ) : (
            <CanView access={[ACCESS.ACCOUNT_DAY_CLOSE.ADD]}>
              <Button color="dark" size="sm" onClick={openCloseModal} disabled={!report}>
                <i className="mdi mdi-lock-outline me-1" />
                Close Day
              </Button>
            </CanView>
          )}
          <Button color="success" outline size="sm" onClick={handleExportPdf} disabled={isExporting || !report}>
            <i className={`mdi mdi-file-pdf-box me-1 ${isExporting ? "mdi-spin" : ""}`} />
            {isExporting ? "Exporting..." : "Export PDF"}
          </Button>
        </Col>
      </Row>

      {isLoading ? (
        <div className="text-center py-5">
          <Spinner />
        </div>
      ) : !report ? (
        <div className="text-center py-5 text-muted">No data available.</div>
      ) : (
        <>
          <Row className="g-3 mb-3">
            <Col md={3}>
              <Card className="border">
                <CardBody>
                  <div className="text-muted small text-uppercase fw-semibold">Opening (All Accounts)</div>
                  <div className="fs-5 fw-bold">{formatCurrency(report.grand_total.opening_balance)}</div>
                </CardBody>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="border">
                <CardBody>
                  <div className="text-muted small text-uppercase fw-semibold">Total Credit</div>
                  <div className="fs-5 fw-bold text-success">{formatCurrency(report.grand_total.total_credit)}</div>
                </CardBody>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="border">
                <CardBody>
                  <div className="text-muted small text-uppercase fw-semibold">Total Debit</div>
                  <div className="fs-5 fw-bold text-danger">{formatCurrency(report.grand_total.total_debit)}</div>
                </CardBody>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="border">
                <CardBody>
                  <div className="text-muted small text-uppercase fw-semibold">Closing (All Accounts)</div>
                  <div className="fs-5 fw-bold">{formatCurrency(report.grand_total.closing_balance)}</div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Card className="border mb-3">
            <CardBody>
              <h5 className="mb-3">Cash In Hand — By Staff</h5>
              <div className="table-responsive">
                <Table bordered size="sm" className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Custodian</th>
                      <th className="text-end">Opening</th>
                      <th className="text-end">Received Today</th>
                      <th className="text-end">Deposited Today</th>
                      <th className="text-end">Refunds Today</th>
                      <th className="text-end">Closing</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.cash_summary.length === 0 ? (
                      <tr><td colSpan={7} className="text-center text-muted">No cash accounts configured.</td></tr>
                    ) : (
                      report.cash_summary.map(cash => (
                        <tr key={cash.account_id}>
                          <td>{cash.custodian || cash.account_name}</td>
                          <td className="text-end">{formatCurrency(cash.opening_balance)}</td>
                          <td className="text-end text-success">{formatCurrency(cash.received_today)}</td>
                          <td className="text-end">{formatCurrency(cash.deposited_today)}</td>
                          <td className="text-end text-danger">{formatCurrency(cash.refunds_paid_today)}</td>
                          <td className="text-end fw-semibold">{formatCurrency(cash.closing_balance)}</td>
                          <td>
                            {cash.is_overdue ? (
                              <Badge color="danger" title={`Deposit deadline was ${cash.deposit_deadline}`}>
                                Overdue ({cash.days_overdue}d)
                              </Badge>
                            ) : cash.has_undeposited_cash ? (
                              <Badge color="warning" className="text-dark">Pending</Badge>
                            ) : (
                              <Badge color="success">Clear</Badge>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>

          {report.accounts.map(block => (
            <Card className="border mb-3" key={block.account.id}>
              <CardBody>
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <h5 className="mb-0">
                    {block.account.name}
                    <span className="text-muted small ms-2">
                      ({block.account.account_type}{block.account.custodian ? ` — ${block.account.custodian}` : ""})
                    </span>
                  </h5>
                  <div className="small text-muted">
                    Opening {formatCurrency(block.opening_balance, block.account.currency)} → Closing{" "}
                    <strong>{formatCurrency(block.closing_balance, block.account.currency)}</strong>
                  </div>
                </div>
                <div className="table-responsive">
                  <Table bordered size="sm" className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Date</th>
                        <th>Description</th>
                        <th>Reference</th>
                        <th className="text-end">Credit</th>
                        <th className="text-end">Debit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {block.transactions.length === 0 ? (
                        <tr><td colSpan={5} className="text-center text-muted">No transactions.</td></tr>
                      ) : (
                        block.transactions.map(txn => {
                          const isCredit = txn.transaction_type?.slug === "credit" || txn.transactionType?.slug === "credit"
                          return (
                            <tr key={txn.id}>
                              <td>{formatReadableDate(txn.date)}</td>
                              <td>{txn.description || "-"}</td>
                              <td className="text-muted small">{txn.reference_type || "-"}</td>
                              <td className="text-end text-success">{isCredit ? formatCurrency(txn.amount, block.account.currency) : ""}</td>
                              <td className="text-end text-danger">{!isCredit ? formatCurrency(txn.amount, block.account.currency) : ""}</td>
                            </tr>
                          )
                        })
                      )}
                      <tr className="table-light fw-semibold">
                        <td colSpan={3}>Total</td>
                        <td className="text-end text-success">{formatCurrency(block.total_credit, block.account.currency)}</td>
                        <td className="text-end text-danger">{formatCurrency(block.total_debit, block.account.currency)}</td>
                      </tr>
                    </tbody>
                  </Table>
                </div>
              </CardBody>
            </Card>
          ))}

          <Row className="g-3">
            <Col md={6}>
              <Card className="border">
                <CardBody>
                  <h5 className="mb-3">Refunds Today</h5>
                  <div className="table-responsive">
                    <Table bordered size="sm" className="mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Customer</th>
                          <th>Method</th>
                          <th>Account</th>
                          <th className="text-end">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.refunds.length === 0 ? (
                          <tr><td colSpan={4} className="text-center text-muted">No refunds today.</td></tr>
                        ) : (
                          report.refunds.map(refund => (
                            <tr key={refund.id}>
                              <td>{refund.customer?.name || "-"}</td>
                              <td>{refund.payment_method?.name || refund.paymentMethod?.name || "-"}</td>
                              <td>{refund.account?.name || "-"}</td>
                              <td className="text-end text-danger">{formatCurrency(refund.amount)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="border">
                <CardBody>
                  <h5 className="mb-3">Expenses Today</h5>
                  <div className="table-responsive">
                    <Table bordered size="sm" className="mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Type</th>
                          <th>Account</th>
                          <th className="text-end">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.expenses.length === 0 ? (
                          <tr><td colSpan={3} className="text-center text-muted">No expenses today.</td></tr>
                        ) : (
                          report.expenses.map(expense => (
                            <tr key={expense.id}>
                              <td>{expense.expense_type?.name || expense.expenseType?.name || "-"}</td>
                              <td>{expense.account?.name || "-"}</td>
                              <td className="text-end text-danger">{formatCurrency(expense.amount)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </>
      )}

      <Modal isOpen={closeModal} toggle={() => setCloseModal(false)}>
        <ModalHeader toggle={() => setCloseModal(false)}>Close {date}</ModalHeader>
        <ModalBody>
          {overdueCash ? (
            <>
              <div className="alert alert-danger py-2 px-3 mb-3" style={{ fontSize: 13 }}>
                Overdue undeposited cash found — the deposit deadline for these accounts has already passed.
              </div>
              <Table bordered size="sm" className="mb-3">
                <thead className="table-light">
                  <tr>
                    <th>Custodian / Account</th>
                    <th className="text-end">Amount</th>
                    <th>Deadline was</th>
                  </tr>
                </thead>
                <tbody>
                  {overdueCash.map(row => (
                    <tr key={row.account_id}>
                      <td>{row.custodian || row.account_name}</td>
                      <td className="text-end text-danger">{formatCurrency(row.amount)}</td>
                      <td>{row.deposit_deadline} ({row.days_overdue}d overdue)</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <div className="form-check mb-2">
                <Input
                  type="checkbox"
                  id="acknowledge_overdue"
                  className="form-check-input"
                  checked={acknowledgeOverdue}
                  onChange={e => setAcknowledgeOverdue(e.target.checked)}
                />
                <Label className="form-check-label" htmlFor="acknowledge_overdue">
                  I acknowledge the overdue cash above and want to close this day anyway.
                </Label>
              </div>
              {!acknowledgeOverdue && (
                <FormFeedback className="d-block">Check the box to proceed.</FormFeedback>
              )}
            </>
          ) : (
            <div className="mb-3">
              <Label htmlFor="close_notes">Notes (optional)</Label>
              <Input
                type="textarea"
                id="close_notes"
                value={closeNotes}
                onChange={e => setCloseNotes(e.target.value)}
              />
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setCloseModal(false)}>Cancel</Button>
          <Button
            color="dark"
            onClick={handleConfirmClose}
            disabled={closingDay || (overdueCash && !acknowledgeOverdue)}
          >
            {closingDay ? "Closing..." : overdueCash ? "Close Anyway" : "Close Day"}
          </Button>
        </ModalFooter>
      </Modal>
    </PageWrapper>
  )
}

export default AccountDailyReport
